import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
  alpha,
  Fade,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import StoreIcon from '@mui/icons-material/Store';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';
import ImageUploadModal from './ImageUploadModal';
import { formatCep } from '../utils/cep';

const WEEK_DAYS = [
  { id: 0, label: 'Domingo' },
  { id: 1, label: 'Segunda-feira' },
  { id: 2, label: 'Terça-feira' },
  { id: 3, label: 'Quarta-feira' },
  { id: 4, label: 'Quinta-feira' },
  { id: 5, label: 'Sexta-feira' },
  { id: 6, label: 'Sábado' }
];

const createWeeklySchedule = (schedule, opening = '17:00', closing = '00:00') =>
  WEEK_DAYS.reduce((result, day) => {
    const saved = schedule?.[day.id] || schedule?.[String(day.id)];
    result[day.id] = {
      enabled: saved?.enabled !== undefined ? Boolean(saved.enabled) : true,
      open: saved?.open || opening,
      close: saved?.close || closing
    };
    return result;
  }, {});

function Lanchonetes() {
  const theme = useTheme();
  const [lanchonetes, setLanchonetes] = useState([]);
  const [stripeOverview, setStripeOverview] = useState({ configured: false, stores: [] });
  const [paymentBusy, setPaymentBusy] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [waiterDialogOpen, setWaiterDialogOpen] = useState(false);
  const [editingLanchonete, setEditingLanchonete] = useState(null);
  const [imageUploadModalOpen, setImageUploadModalOpen] = useState(false);
  const [selectedLanchonete, setSelectedLanchonete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waiterData, setWaiterData] = useState({ name: '', email: '', password: '' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    cep: '',
    city: '',
    phone: '',
    email: '',
    opening_hours: '',
    opening_time: '17:00',
    closing_time: '00:00',
    weekly_schedule: createWeeklySchedule(),
    delivery_fee: 0,
    min_order_value: 0,
    active: true,
    is_online: true,
    type: 'lanchonete',
    image: null,
    free_delivery_active: false,
    free_delivery_threshold: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [returnToOnboarding] = useState(
    () => new URLSearchParams(window.location.search).get('from') === 'onboarding'
  );
  const { isAdminMaster, user } = useAuth();
  const isMaster = user?.user_type === 'admin_master';
  const handleOpenDialogRef = useRef(null);

  const loadLanchonetes = useCallback(async () => {
    try {
      setLoading(true);
      if (isMaster) {
        // A listagem administrativa inclui as lanchonetes retiradas do app.
        const [response, stripeResponse] = await Promise.all([
          api.get('/admin/lanchonetes', { params: { limit: 1000 } }),
          api.get('/stripe-production/overview').catch(() => ({ data: { configured: false, stores: [] } }))
        ]);
        setLanchonetes(response.data.lanchonetes || []);
        setStripeOverview(stripeResponse.data);
      } else {
        const response = await api.get(`/lanchonetes/${user?.lanchonete_id}`);
        setLanchonetes(response.data ? [response.data] : []);
        if (response.data && new URLSearchParams(window.location.search).get('edit') === '1') {
          handleOpenDialogRef.current?.(response.data);
          window.history.replaceState({}, '', '/admin/lanchonetes');
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar lanchonetes');
      console.error('Erro ao carregar lanchonetes:', error);
    } finally {
      setLoading(false);
    }
  }, [isMaster, user?.lanchonete_id]);

  const handleOpenDialog = (lanchonete = null) => {
    if (lanchonete) {
      setEditingLanchonete(lanchonete);
      setFormData({
        name: lanchonete.name || '',
        description: lanchonete.description || '',
        address: lanchonete.address || '',
        cep: lanchonete.cep || '',
        city: lanchonete.city || '',
        phone: lanchonete.phone || '',
        email: lanchonete.email || '',
        opening_hours: lanchonete.opening_hours || '',
        opening_time: lanchonete.opening_time || '17:00',
        closing_time: lanchonete.closing_time || '00:00',
        weekly_schedule: createWeeklySchedule(
          lanchonete.weekly_schedule,
          lanchonete.opening_time || '17:00',
          lanchonete.closing_time || '00:00'
        ),
        delivery_fee: lanchonete.delivery_fee || 0,
        min_order_value: lanchonete.min_order_value || 0,
        active: Boolean(lanchonete.active),
        is_online: Boolean(lanchonete.is_online),
        type: lanchonete.type || 'lanchonete',
        image: lanchonete.image || null,
        free_delivery_active: Boolean(lanchonete.free_delivery_active),
        free_delivery_threshold: lanchonete.free_delivery_threshold || ''
      });
    } else {
      setEditingLanchonete(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        cep: '',
        city: '',
        phone: '',
        email: '',
        opening_hours: '',
        opening_time: '17:00',
        closing_time: '00:00',
        weekly_schedule: createWeeklySchedule(),
        delivery_fee: 0,
        min_order_value: 0,
        active: true,
        is_online: true,
        type: 'lanchonete',
        image: null,
        free_delivery_active: false,
        free_delivery_threshold: ''
      });
    }
    setLogoPreview(lanchonete?.image || null);
    setDialogOpen(true);
  };

  handleOpenDialogRef.current = handleOpenDialog;

  useEffect(() => {
    loadLanchonetes();
  }, [loadLanchonetes]);

  const handleImageUpload = (lanchonete) => {
    setSelectedLanchonete(lanchonete);
    setImageUploadModalOpen(true);
  };

  const handleOpenWaiterDialog = (lanchonete) => {
    setSelectedLanchonete(lanchonete);
    setWaiterData({ name: '', email: '', password: '' });
    setWaiterDialogOpen(true);
  };

  const handleCreateWaiter = async () => {
    try {
      await api.post('/users/waiter', {
        ...waiterData,
        lanchonete_id: selectedLanchonete.id
      });
      toast.success('Garçom criado com sucesso!');
      setWaiterDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar garçom');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'pizzaria': return '🍕';
      case 'sorveteria': return '🍦';
      case 'agua': return '💧';
      case 'farmacia': return '💊';
      case 'restaurante': return '🍽️';
      case 'cafeteria': return '☕';
      case 'conveniencia': return '🏪';
      case 'outro': return '🏠';
      default: return '🍔';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'lanchonete': return 'Lanchonete';
      case 'pizzaria': return 'Pizzaria';
      case 'sorveteria': return 'Sorveteria';
      case 'agua': return 'Água/Copo';
      case 'farmacia': return 'Farmácia';
      case 'restaurante': return 'Restaurante';
      case 'cafeteria': return 'Cafeteria';
      case 'conveniencia': return 'Conveniência';
      case 'outro': return 'Outro';
      default: return 'Lanchonete';
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLanchonete(null);
    setFormData({
      name: '',
      description: '',
      address: '',
      cep: '',
      city: '',
      phone: '',
      email: '',
      opening_hours: '',
      opening_time: '17:00',
      closing_time: '00:00',
      weekly_schedule: createWeeklySchedule(),
      delivery_fee: 0,
      min_order_value: 0,
      active: true,
      is_online: true,
      type: 'lanchonete',
      image: null
    });
    setLogoPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScheduleChange = (dayId, field, value) => {
    setFormData((previous) => ({
      ...previous,
      weekly_schedule: {
        ...previous.weekly_schedule,
        [dayId]: {
          ...previous.weekly_schedule[dayId],
          [field]: value
        }
      }
    }));
  };

  const handleCepBlur = async () => {
    const cep = String(formData.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          city: data.localidade ? `${data.localidade} - ${data.uf}` : prev.city,
          address: prev.address || `${data.logradouro || ''}, ${data.bairro || ''} - ${data.localidade || ''} - ${data.uf || ''}`
        }));
      }
    } catch (_) {
      // ignore
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (String(formData.cep || '').replace(/\D/g, '').length !== 8 || !String(formData.city || '').trim()) {
      toast.error('Informe um CEP válido para preencher a cidade da lanchonete.');
      return;
    }
    
    try {
      let lanchoneteResponse;
      const { image, ...lanchoneteData } = formData;
      
      // Debug: Verificar dados sendo enviados
      console.log('Dados do formulário:', formData);
      
      if (editingLanchonete) {
        lanchoneteResponse = await api.put(`/lanchonetes/${editingLanchonete.id}`, lanchoneteData);
        toast.success('Lanchonete atualizada com sucesso!');
      } else {
        lanchoneteResponse = await api.post('/lanchonetes', lanchoneteData);
        console.log('Lanchonete criada response:', lanchoneteResponse);
        console.log('Lanchonete criada data:', lanchoneteResponse.data);
        toast.success('Lanchonete criada com sucesso!');
      }
      
      // Se há uma imagem para fazer upload
      if (formData.image && formData.image instanceof File) {
        const lanchoneteId = editingLanchonete ? editingLanchonete.id : lanchoneteResponse.data.lanchonete.id;
        console.log('editingLanchonete:', editingLanchonete);
        console.log('lanchoneteResponse.data.lanchonete.id:', lanchoneteResponse.data.lanchonete.id);
        console.log('Lanchonete ID para upload:', lanchoneteId);
        console.log('Imagem para upload:', formData.image);
        
        const uploadFormData = new FormData();
        uploadFormData.append('image', formData.image);
        
        console.log('Enviando upload para:', `/lanchonetes/${lanchoneteId}/upload-image`);
        
        try {
          const uploadResponse = await api.post(`/lanchonetes/${lanchoneteId}/upload-image`, uploadFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          console.log('Upload response:', uploadResponse);
          toast.success('Logo da lanchonete enviada com sucesso!');
        } catch (uploadError) {
          console.error('Erro no upload:', uploadError);
          console.error('Response:', uploadError.response);
          throw uploadError;
        }
      }
      
      handleCloseDialog();
      if (returnToOnboarding) {
        window.location.href = '/onboarding';
        return;
      }
      // Recarregar lanchonetes APÓS o upload da imagem (se houver)
      loadLanchonetes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar lanchonete');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta lanchonete?')) {
      try {
        await api.delete(`/lanchonetes/${id}`);
        toast.success('Lanchonete excluída com sucesso!');
        loadLanchonetes();
      } catch (error) {
        toast.error('Erro ao excluir lanchonete');
      }
    }
  };

  const handleToggleStatus = async (lanchonete) => {
    const active = !Boolean(lanchonete.active);
    const action = active ? 'colocar novamente no aplicativo' : 'retirar do aplicativo';

    if (!window.confirm(`Deseja ${action} a lanchonete "${lanchonete.name}"?`)) {
      return;
    }

    try {
      await api.patch(`/lanchonetes/${lanchonete.id}/active`, { active });
      toast.success(
        active
          ? 'Lanchonete colocada novamente no aplicativo!'
          : 'Lanchonete retirada do aplicativo sem apagar os dados!'
      );
      loadLanchonetes();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleToggleOnlineStatus = async (id, isOnline) => {
    try {
      await api.put(`/lanchonetes/${id}/online-status`, { is_online: isOnline });
      toast.success(`Lanchonete ${isOnline ? 'online' : 'offline'} com sucesso!`);
      loadLanchonetes();
    } catch (error) {
      toast.error('Erro ao alterar status online/offline');
    }
  };

  const getStripeStore = (id) => stripeOverview.stores?.find((item) => Number(item.id) === Number(id));

  const handleStripeOnboarding = async (lanchonete) => {
    try {
      setPaymentBusy(`connect-${lanchonete.id}`);
      const current = getStripeStore(lanchonete.id)?.stripe;
      const response = current?.connected
        ? await api.post(`/stripe-production/accounts/lanchonete/${lanchonete.id}/onboarding-link`, { return_path: '/admin/lanchonetes' })
        : await api.post('/stripe-production/accounts', { entity_type: 'lanchonete', entity_id: lanchonete.id, return_path: '/admin/lanchonetes' });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao abrir cadastro Stripe');
      setPaymentBusy('');
    }
  };

  const handlePaymentProvider = async (lanchonete, provider) => {
    if (!window.confirm(`Mudar os novos pagamentos de "${lanchonete.name}" para ${provider === 'stripe' ? 'Stripe' : 'Mercado Pago'}? A lanchonete deve estar fechada.`)) return;
    try {
      setPaymentBusy(`provider-${lanchonete.id}`);
      await api.put(`/stripe-production/lanchonetes/${lanchonete.id}/provider`, { payment_provider: provider });
      toast.success(`Novos pedidos usarão ${provider === 'stripe' ? 'Stripe' : 'Mercado Pago'}.`);
      await loadLanchonetes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao trocar provedor');
    } finally {
      setPaymentBusy('');
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.contrastText,
                mb: 2
              }} 
            />
            <Typography variant="h6" color="inherit">
              Carregando lanchonetes...
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
              Preparando suas informações
            </Typography>
          </Box>
        </Fade>
        <LinearProgress 
          sx={{ 
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }} 
        />
      </Box>
    );
  }

  const canAccess = isAdminMaster() || (user?.user_type === 'admin_lanchonete' && user?.lanchonete_id);

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Acesso restrito. Apenas administradores autorizados podem gerenciar lanchonetes.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {isAdminMaster() ? 'Lanchonetes' : 'Minha Lanchonete'}
        </Typography>
        {isAdminMaster() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nova Lanchonete
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Funcionamento</TableCell>
              <TableCell>Taxa Entrega</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Online/Offline</TableCell>
              {isAdminMaster() && <TableCell>Pagamento</TableCell>}
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lanchonetes.map((lanchonete) => (
              <TableRow key={lanchonete.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {lanchonete.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {lanchonete.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {lanchonete.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {getTypeIcon(lanchonete.type)} {getTypeLabel(lanchonete.type)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    {lanchonete.phone && (
                      <Box display="flex" alignItems="center" mb={1}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{lanchonete.phone}</Typography>
                      </Box>
                    )}
                    {lanchonete.email && (
                      <Box display="flex" alignItems="center">
                        <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{lanchonete.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {lanchonete.opening_hours && (
                    <Box display="flex" alignItems="center">
                      <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">{lanchonete.opening_hours}</Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {lanchonete.delivery_fee > 0 
                      ? `R$ ${parseFloat(lanchonete.delivery_fee || 0).toFixed(2)}` 
                      : 'Grátis'
                    }
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: lanchonete.is_open ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                        mb: 1
                      }}
                    >
                      {lanchonete.status_text || (lanchonete.active ? 'Ativa' : 'Inativa')}
                    </Typography>
                    {isAdminMaster() && (
                      <Button
                        size="small"
                        variant={lanchonete.active ? 'outlined' : 'contained'}
                        color={lanchonete.active ? 'error' : 'success'}
                        startIcon={lanchonete.active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        onClick={() => handleToggleStatus(lanchonete)}
                      >
                        {lanchonete.active ? 'Retirar do app' : 'Colocar no app'}
                      </Button>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: lanchonete.is_online ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                        mb: 1
                      }}
                    >
                      {lanchonete.is_online ? 'Online' : 'Offline'}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(lanchonete.is_online)}
                          onChange={(e) => handleToggleOnlineStatus(lanchonete.id, e.target.checked)}
                          color="success"
                          size="small"
                        />
                      }
                      label={lanchonete.is_online ? 'Online' : 'Offline'}
                    />
                  </Box>
                </TableCell>
                {isAdminMaster() && (
                  <TableCell sx={{ minWidth: 230 }}>
                    {(() => {
                      const payment = getStripeStore(lanchonete.id);
                      const stripe = payment?.stripe || {};
                      const provider = payment?.payment_provider || lanchonete.payment_provider || 'mercadopago';
                      return (
                        <Stack spacing={1} alignItems="flex-start">
                          <Chip
                            size="small"
                            color={provider === 'stripe' ? 'primary' : 'info'}
                            label={provider === 'stripe' ? 'Usando Stripe' : 'Usando Mercado Pago'}
                          />
                          <Chip
                            size="small"
                            color={stripe.ready ? 'success' : 'warning'}
                            label={stripe.ready ? 'Stripe pronta' : stripe.connected ? 'Cadastro Stripe pendente' : 'Stripe não conectada'}
                          />
                          {!stripe.ready && (
                            <Button size="small" variant="outlined" disabled={!stripeOverview.configured || Boolean(paymentBusy)} onClick={() => handleStripeOnboarding(lanchonete)}>
                              {stripe.connected ? 'Continuar cadastro' : 'Conectar Stripe'}
                            </Button>
                          )}
                          {stripe.ready && provider !== 'stripe' && (
                            <Button size="small" variant="contained" disabled={Boolean(paymentBusy)} onClick={() => handlePaymentProvider(lanchonete, 'stripe')}>
                              Mudar para Stripe
                            </Button>
                          )}
                          {provider === 'stripe' && (
                            <Button size="small" color="warning" variant="outlined" disabled={Boolean(paymentBusy)} onClick={() => handlePaymentProvider(lanchonete, 'mercadopago')}>
                              Voltar ao Mercado Pago
                            </Button>
                          )}
                        </Stack>
                      );
                    })()}
                  </TableCell>
                )}
                <TableCell align="center">
                  {isAdminMaster() && (
                    <Tooltip title="Adicionar Garçom">
                      <IconButton
                        color="secondary"
                        onClick={() => handleOpenWaiterDialog(lanchonete)}
                      >
                        <PersonAddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(lanchonete)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="info"
                    onClick={() => handleImageUpload(lanchonete)}
                  >
                    <StoreIcon />
                  </IconButton>
                  {isAdminMaster() && (
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(lanchonete.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de criação/edição */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLanchonete ? 'Editar Lanchonete' : 'Nova Lanchonete'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Upload de Logo */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Logo da Lanchonete
                </Typography>
                <Box 
                  sx={{ 
                    border: '2px dashed #ccc', 
                    p: 2, 
                    mb: 2, 
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: '#E50914' }
                  }}
                  onClick={() => document.getElementById('logo-upload-input').click()}
                >
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8 }}
                    />
                  ) : (
                    <Box>
                      <StoreIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Clique para adicionar o logo
                      </Typography>
                    </Box>
                  )}
                </Box>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoSelect}
                  style={{ display: 'none' }}
                  id="logo-upload-input"
                />
                {formData.image && formData.image instanceof File && (
                  <Typography variant="body2" color="primary">
                    Arquivo selecionado: {formData.image.name}
                  </Typography>
                )}
              </Box>
              
              <TextField
                name="name"
                label="Nome"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Tipo de Estabelecimento</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Tipo de Estabelecimento"
                >
                  <MenuItem value="lanchonete">🍔 Lanchonete</MenuItem>
                  <MenuItem value="pizzaria">🍕 Pizzaria</MenuItem>
                  <MenuItem value="sorveteria">🍦 Sorveteria</MenuItem>
                  <MenuItem value="agua">💧 Água/Copo</MenuItem>
                  <MenuItem value="farmacia">💊 Farmácia</MenuItem>
                  <MenuItem value="restaurante">🍽️ Restaurante</MenuItem>
                  <MenuItem value="cafeteria">☕ Cafeteria</MenuItem>
                  <MenuItem value="conveniencia">🏪 Conveniência</MenuItem>
                  <MenuItem value="outro">🏠 Outro</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="description"
                label="Descrição"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                name="cep"
                label="CEP"
                value={formData.cep}
                onChange={(event) => setFormData((previous) => ({ ...previous, cep: formatCep(event.target.value), city: '' }))}
                onBlur={handleCepBlur}
                fullWidth
                required
                inputProps={{ maxLength: 9, inputMode: 'numeric' }}
              />
              <TextField
                fullWidth
                label="Cidade identificada pelo CEP"
                value={formData.city}
                required
                InputProps={{ readOnly: true }}
                helperText="Preenchida automaticamente ao informar um CEP válido."
              />
              <TextField
                name="address"
                label="Endereço"
                value={formData.address}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="phone"
                label="Telefone"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
              />

              <TextField
                name="opening_hours"
                label="Horário de Funcionamento (texto)"
                value={formData.opening_hours}
                onChange={handleInputChange}
                fullWidth
                helperText="Ex: Seg-Sex: 17:00-00:00"
              />
              <Box display="flex" gap={2}>
                <TextField
                  name="opening_time"
                  label="Horário de Abertura"
                  type="time"
                  value={formData.opening_time}
                  onChange={handleInputChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  name="closing_time"
                  label="Horário de Fechamento"
                  type="time"
                  value={formData.closing_time}
                  onChange={handleInputChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>

              <Box
                sx={{
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Programação semanal automática
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Marque os dias em que a lanchonete abre. Nos dias desmarcados ela ficará fechada automaticamente.
                </Typography>

                {WEEK_DAYS.map((day) => {
                  const schedule = formData.weekly_schedule[day.id];
                  return (
                    <Box
                      key={day.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        borderTop: day.id === 0 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        flexWrap: 'wrap'
                      }}
                    >
                      <FormControlLabel
                        sx={{ minWidth: 180 }}
                        control={
                          <Switch
                            checked={Boolean(schedule.enabled)}
                            onChange={(event) => handleScheduleChange(day.id, 'enabled', event.target.checked)}
                          />
                        }
                        label={schedule.enabled ? day.label : `${day.label} — Fechado`}
                      />
                      <TextField
                        label="Abre às"
                        type="time"
                        size="small"
                        value={schedule.open}
                        disabled={!schedule.enabled}
                        onChange={(event) => handleScheduleChange(day.id, 'open', event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Fecha às"
                        type="time"
                        size="small"
                        value={schedule.close}
                        disabled={!schedule.enabled}
                        onChange={(event) => handleScheduleChange(day.id, 'close', event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  );
                })}
              </Box>
              <TextField
                name="delivery_fee"
                label="Taxa de Entrega"
                type="number"
                value={formData.delivery_fee}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                name="min_order_value"
                label="Valor Mínimo do Pedido"
                type="number"
                value={formData.min_order_value}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />

              {/* Configurações de Frete Grátis */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1, my: 1, width: '100%' }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="free_delivery_active"
                      checked={Boolean(formData.free_delivery_active)}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label="Oferecer Frete Grátis para valor mínimo"
                />

                {formData.free_delivery_active && (
                  <TextField
                    name="free_delivery_threshold"
                    label="Valor Mínimo para Frete Grátis"
                    type="number"
                    value={formData.free_delivery_threshold}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={Boolean(formData.active)}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="Lanchonete Ativa"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_online"
                    checked={Boolean(formData.is_online)}
                    onChange={handleInputChange}
                    color="success"
                  />
                }
                label="Lanchonete Online (aceita pedidos)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingLanchonete ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal de Upload de Imagem */}
      <ImageUploadModal
        open={imageUploadModalOpen}
        onClose={() => setImageUploadModalOpen(false)}
        onUpload={(imageData) => {
          console.log('Imagem enviada:', imageData);
          toast.success('Imagem da lanchonete atualizada com sucesso!');
          setImageUploadModalOpen(false);
          // Recarregar dados para mostrar a nova imagem
          window.location.reload();
        }}
        title={`Upload de Logo - ${selectedLanchonete?.name || 'Nova Lanchonete'}`}
        selectedLanchonete={selectedLanchonete}
      />

      {/* Dialog Novo Garçom */}
      <Dialog open={waiterDialogOpen} onClose={() => setWaiterDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white' }}>
          Novo Garçom - {selectedLanchonete?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nome" margin="normal" value={waiterData.name} onChange={(e) => setWaiterData({ ...waiterData, name: e.target.value })} />
          <TextField fullWidth label="Email" margin="normal" value={waiterData.email} onChange={(e) => setWaiterData({ ...waiterData, email: e.target.value })} />
          <TextField fullWidth label="Senha" type="password" margin="normal" value={waiterData.password} onChange={(e) => setWaiterData({ ...waiterData, password: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaiterDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleCreateWaiter}>Criar Garçom</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Lanchonetes;
